import { useState, useEffect, useRef, useCallback } from 'react';
import { Loader2, Eye, Play, RefreshCw, RotateCcw } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { reviewRecomputeAPI } from '../../lib/api';
import { toast } from 'sonner';
import { getErrorMessage } from '../../lib/errors';

// Same chain/job-poll shape as WiseReferenceBackfillPanel.jsx, adapted for
// reroute_review's outcomes (exited/narrowed/unchanged/skipped_conflict/
// not_eligible instead of that module's updated/no_payer_reference/
// unresolvable). This is a pure re-evaluation of services/review_routing.py
// against each doc's current fields -- no external API call per doc, so
// runs are fast, but still chunked_chain per CLAUDE.md section 13 since the
// cohort (every pending_review doc) is not provably bounded.
const POLL_INTERVAL_MS = 2000;
const HEARTBEAT_STALE_SECONDS = 60;

function fmtInt(n) {
  if (n == null) return '-';
  return Number(n).toLocaleString('en-GB');
}

function secondsSince(isoString) {
  if (!isoString) return null;
  const then = new Date(isoString).getTime();
  if (Number.isNaN(then)) return null;
  return Math.max(0, Math.floor((Date.now() - then) / 1000));
}

function useJobPolling({ onCompleted, onFailed, onProgress }) {
  const [polling, setPolling] = useState(false);
  const [elapsed, setElapsed] = useState(0);
  const pollRef = useRef(null);
  const elapsedRef = useRef(null);
  const startTimeRef = useRef(null);

  const stop = useCallback(() => {
    if (pollRef.current) { clearInterval(pollRef.current); pollRef.current = null; }
    if (elapsedRef.current) { clearInterval(elapsedRef.current); elapsedRef.current = null; }
    startTimeRef.current = null;
    setPolling(false);
  }, []);

  useEffect(() => stop, [stop]);

  const start = useCallback((jobId) => {
    stop();
    setPolling(true);
    setElapsed(0);
    startTimeRef.current = Date.now();

    elapsedRef.current = setInterval(() => {
      setElapsed(Math.floor((Date.now() - startTimeRef.current) / 1000));
    }, 1000);

    pollRef.current = setInterval(async () => {
      try {
        const { data } = await reviewRecomputeAPI.jobStatus(jobId);
        if (data.status === 'completed') {
          stop();
          onCompleted(data.result);
        } else if (data.status === 'failed') {
          stop();
          onFailed(data.error, data.progress || null);
        } else if (data.status === 'running') {
          onProgress(data.progress || null);
        }
      } catch (err) {
        stop();
        toast.error(getErrorMessage(err));
      }
    }, POLL_INTERVAL_MS);
  }, [stop, onCompleted, onFailed, onProgress]);

  return { polling, elapsed, start };
}

const SAMPLE_DISPLAY_LIMIT = 10;

function ReasonsCell({ reasons }) {
  if (!reasons || reasons.length === 0) return <span className="text-muted-foreground">(none)</span>;
  return <span>{reasons.join('; ')}</span>;
}

function SampleRows({ operation, samples }) {
  if (!samples || samples.length === 0) return null;
  const shown = samples.slice(0, SAMPLE_DISPLAY_LIMIT);
  return (
    <div className="text-xs space-y-1 pt-1" data-testid={`review-recompute-${operation}-samples`}>
      <p className="text-muted-foreground">
        Sample docs (first {shown.length} of {samples.length}) -- reasons before vs. after recompute:
      </p>
      <div className="border rounded overflow-x-auto">
        <table className="w-full text-xs">
          <thead>
            <tr className="border-b bg-muted/50">
              <th className="text-left font-medium px-2 py-1">Invoice</th>
              <th className="text-left font-medium px-2 py-1">Reasons before</th>
              <th className="text-left font-medium px-2 py-1">Reasons after</th>
              <th className="text-left font-medium px-2 py-1">Outcome</th>
            </tr>
          </thead>
          <tbody>
            {shown.map((s) => (
              <tr key={s.id} className="border-b last:border-b-0">
                <td className="px-2 py-1 font-mono">{s.id}</td>
                <td className="px-2 py-1 text-muted-foreground"><ReasonsCell reasons={s.prior_reasons} /></td>
                <td className="px-2 py-1"><ReasonsCell reasons={s.current_reasons} /></td>
                <td className="px-2 py-1">{s.current_review_status ?? '-'}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function ChainSection({
  operation, title, icon, chain, progress, failed, polling, elapsed, starting, stopping,
  onRun, onStop, runLabel, runDisabled, showSamples,
}) {
  const running = chain?.status === 'running';
  const live = progress || chain?.seed;
  const cohortCount = chain?.metadata?.cohort_count;
  const processed = live?.cohort_processed;
  const heartbeatAge = secondsSince(chain?.heartbeat_at);
  const stalled = running && !polling && heartbeatAge != null && heartbeatAge > HEARTBEAT_STALE_SECONDS;
  const errorText = (chain?.status === 'error' && chain?.error) || failed;
  const tallies = live?.tallies || {};

  return (
    <div className="border rounded-lg p-3 space-y-2">
      <div className="flex items-center gap-2 flex-wrap">
        <span className="text-sm font-medium flex items-center gap-1">{icon}{title}</span>
        <Button data-testid={`review-recompute-${operation}-run`} onClick={onRun} disabled={runDisabled} size="sm">
          {(starting || polling) ? <Loader2 className="h-3 w-3 animate-spin mr-1" /> : <Eye className="h-3 w-3 mr-1" />}
          {running
            ? `Chain running, run ${fmtInt(chain.runs_count)}, ${fmtInt(processed)}/${fmtInt(cohortCount)}`
            : runLabel}
        </Button>
        {running && (
          <Button data-testid={`review-recompute-${operation}-stop`} onClick={onStop} disabled={stopping} size="sm" variant="outline">
            {stopping ? <Loader2 className="h-3 w-3 animate-spin mr-1" /> : null}
            Stop Chain
          </Button>
        )}
        {polling && (
          <span className="text-xs text-muted-foreground tabular-nums flex items-center gap-1">
            <RefreshCw className="h-3 w-3 animate-spin" />
            {elapsed}s
          </span>
        )}
      </div>

      {chain && (
        <div className="text-xs space-y-1" data-testid={`review-recompute-${operation}-chain-summary`}>
          <div className="flex items-center gap-1">
            <Badge variant={running ? 'default' : chain.status === 'error' ? 'destructive' : 'outline'}>
              {chain.status}
            </Badge>
            <span className="text-muted-foreground">
              run {fmtInt(chain.runs_count)}
              {cohortCount != null && <> -- {fmtInt(processed)} of {fmtInt(cohortCount)} processed</>}
            </span>
            {stalled && (
              <span className="text-amber-600 dark:text-amber-400">
                last active {heartbeatAge}s ago -- watchdog will resume it
              </span>
            )}
          </div>

          {live && (
            <div className="flex flex-wrap gap-3">
              {Object.entries(tallies).filter(([, count]) => count > 0).map(([key, count]) => (
                <span key={key} className={key === 'skipped_conflict' || key === 'not_eligible' || key === 'would_skip_not_eligible' ? 'text-amber-600' : ''}>
                  {key}: <strong>{fmtInt(count)}</strong>
                </span>
              ))}
            </div>
          )}

          {errorText && <p className="text-red-600 dark:text-red-400">{errorText}</p>}

          {showSamples && Object.entries(live?.samples || {}).map(([key, rows]) => (
            rows.length > 0 && <SampleRows key={key} operation={`${operation}-${key}`} samples={rows} />
          ))}
        </div>
      )}
    </div>
  );
}

export function ReceiptCohortNormalizationRecomputePanel() {
  const [previewChain, setPreviewChain] = useState(null);
  const [previewProgress, setPreviewProgress] = useState(null);
  const [previewFailed, setPreviewFailed] = useState(null);
  const [previewStarting, setPreviewStarting] = useState(false);
  const [previewStopping, setPreviewStopping] = useState(false);

  const [applyChain, setApplyChain] = useState(null);
  const [applyProgress, setApplyProgress] = useState(null);
  const [applyFailed, setApplyFailed] = useState(null);
  const [applyStarting, setApplyStarting] = useState(false);
  const [applyStopping, setApplyStopping] = useState(false);

  const fetchPreviewChain = useCallback(async () => {
    try {
      const { data } = await reviewRecomputeAPI.previewChainStatus();
      setPreviewChain(data?.chain || null);
    } catch {
      // Non-fatal: the chain summary just won't render/update this tick.
    }
  }, []);

  const fetchApplyChain = useCallback(async () => {
    try {
      const { data } = await reviewRecomputeAPI.applyChainStatus();
      setApplyChain(data?.chain || null);
    } catch {
      // Non-fatal.
    }
  }, []);

  const previewPoll = useJobPolling({
    onCompleted: useCallback((result) => {
      setPreviewProgress(null);
      setPreviewFailed(null);
      fetchPreviewChain();
      toast.success(result?.truncated ? 'Preview run complete -- continuing chain' : 'Preview complete');
    }, [fetchPreviewChain]),
    onFailed: useCallback((error, lastProgress) => {
      setPreviewProgress((prev) => lastProgress ?? prev);
      setPreviewFailed(error);
      fetchPreviewChain();
      toast.error(error ?? 'Preview run failed');
    }, [fetchPreviewChain]),
    onProgress: useCallback((progress) => setPreviewProgress(progress), []),
  });

  const applyPoll = useJobPolling({
    onCompleted: useCallback((result) => {
      setApplyProgress(null);
      setApplyFailed(null);
      fetchApplyChain();
      toast.success(result?.truncated ? 'Apply run complete -- continuing chain' : 'Apply complete');
    }, [fetchApplyChain]),
    onFailed: useCallback((error, lastProgress) => {
      setApplyProgress((prev) => lastProgress ?? prev);
      setApplyFailed(error);
      fetchApplyChain();
      toast.error(error ?? 'Apply run failed');
    }, [fetchApplyChain]),
    onProgress: useCallback((progress) => setApplyProgress(progress), []),
  });

  useEffect(() => {
    (async () => {
      try {
        const { data } = await reviewRecomputeAPI.jobLatest('preview');
        const job = data?.job;
        if (job?.status === 'running') {
          setPreviewProgress(job.progress || null);
          previewPoll.start(job._id);
        } else if (job?.status === 'failed') {
          setPreviewProgress(job.progress || null);
          setPreviewFailed(job.error);
        }
      } catch {
        // Ignore initial status check failures.
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    (async () => {
      try {
        const { data } = await reviewRecomputeAPI.jobLatest('apply');
        const job = data?.job;
        if (job?.status === 'running') {
          setApplyProgress(job.progress || null);
          applyPoll.start(job._id);
        } else if (job?.status === 'failed') {
          setApplyProgress(job.progress || null);
          setApplyFailed(job.error);
        }
      } catch {
        // Ignore initial status check failures.
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => { fetchPreviewChain(); }, [fetchPreviewChain]);
  useEffect(() => { fetchApplyChain(); }, [fetchApplyChain]);

  useEffect(() => {
    if (previewChain?.status !== 'running') return undefined;
    const id = setInterval(fetchPreviewChain, POLL_INTERVAL_MS);
    return () => clearInterval(id);
  }, [previewChain?.status, fetchPreviewChain]);

  useEffect(() => {
    if (applyChain?.status !== 'running') return undefined;
    const id = setInterval(fetchApplyChain, POLL_INTERVAL_MS);
    return () => clearInterval(id);
  }, [applyChain?.status, fetchApplyChain]);

  const runPreview = async () => {
    setPreviewStarting(true);
    setPreviewFailed(null);
    try {
      const { data } = await reviewRecomputeAPI.previewChainStart();
      if (data.job_id) {
        previewPoll.start(data.job_id);
      } else if (data.status === 'chain_running') {
        toast.error('A preview chain is already running -- it will continue on its own.');
      } else if (data.status === 'chain_unavailable') {
        toast.error('Chain became unavailable while starting -- try again.');
      }
      fetchPreviewChain();
    } catch (err) {
      toast.error(getErrorMessage(err));
    } finally {
      setPreviewStarting(false);
    }
  };

  const runApply = async () => {
    const cohortCount = previewChain?.metadata?.cohort_count ?? applyChain?.metadata?.cohort_count;
    if (!window.confirm(
      `Recompute review routing for ${cohortCount ?? 'the'} pending_review invoice(s)? ` +
      'Any doc whose reasons are fully cured exits review with no human approval.'
    )) return;
    setApplyStarting(true);
    setApplyFailed(null);
    try {
      const { data } = await reviewRecomputeAPI.applyChainStart();
      if (data.job_id) {
        applyPoll.start(data.job_id);
      } else if (data.status === 'chain_running') {
        toast.error('An apply chain is already running -- it will continue on its own.');
      } else if (data.status === 'chain_unavailable') {
        toast.error('Chain became unavailable while starting -- try again.');
      }
      fetchApplyChain();
    } catch (err) {
      const msg = getErrorMessage(err);
      toast.error(msg || 'Failed to start Apply');
      setApplyFailed(msg);
    } finally {
      setApplyStarting(false);
    }
  };

  const stopPreviewChain = async () => {
    if (!previewChain?._id) return;
    if (!window.confirm('Stop the running Preview chain? The current run finishes its time window normally.')) return;
    setPreviewStopping(true);
    try {
      await reviewRecomputeAPI.previewChainStop(previewChain._id);
      toast.success('Stop requested -- the chain will halt after its current run finishes.');
      fetchPreviewChain();
    } catch (err) {
      toast.error(getErrorMessage(err));
    } finally {
      setPreviewStopping(false);
    }
  };

  const stopApplyChain = async () => {
    if (!applyChain?._id) return;
    if (!window.confirm('Stop the running Apply chain? The current run finishes its time window normally.')) return;
    setApplyStopping(true);
    try {
      await reviewRecomputeAPI.applyChainStop(applyChain._id);
      toast.success('Stop requested -- the chain will halt after its current run finishes.');
      fetchApplyChain();
    } catch (err) {
      toast.error(getErrorMessage(err));
    } finally {
      setApplyStopping(false);
    }
  };

  return (
    <Card>
      <CardHeader className="pb-2">
        <div className="flex items-center gap-2">
          <RotateCcw className="h-4 w-4 text-muted-foreground" />
          <CardTitle className="text-base">Review Routing Recompute</CardTitle>
        </div>
        <p className="text-xs text-muted-foreground">
          Routing reasons are stamped once at ingestion and never re-evaluated -- a backfill that
          cures the underlying condition (entity assigned, amount written, ...) leaves the doc
          stuck in Pending review forever unless a human approves it. This re-runs the SAME
          route_invoice rules against every pending_review doc's current fields: a doc with zero
          reasons left exits review with no human approval and is immediately eligible for the
          ordinary matcher; a doc with fewer (but still some) reasons has its stored reasons
          narrowed. Never touches a doc a human has already approved/rejected.
        </p>
      </CardHeader>
      <CardContent className="space-y-3">
        <ChainSection
          operation="preview"
          title="Preview"
          icon={<Eye className="h-3 w-3" />}
          chain={previewChain}
          progress={previewProgress}
          failed={previewFailed}
          polling={previewPoll.polling}
          elapsed={previewPoll.elapsed}
          starting={previewStarting}
          stopping={previewStopping}
          onRun={runPreview}
          onStop={stopPreviewChain}
          runLabel="Preview"
          runDisabled={previewStarting || previewPoll.polling || previewChain?.status === 'running'}
          showSamples
        />
        <ChainSection
          operation="apply"
          title="Apply"
          icon={<Play className="h-3 w-3" />}
          chain={applyChain}
          progress={applyProgress}
          failed={applyFailed}
          polling={applyPoll.polling}
          elapsed={applyPoll.elapsed}
          starting={applyStarting}
          stopping={applyStopping}
          onRun={runApply}
          onStop={stopApplyChain}
          runLabel={previewChain?.metadata?.cohort_count != null ? `Apply (${fmtInt(previewChain.metadata.cohort_count)} candidates)` : 'Apply'}
          runDisabled={applyStarting || applyPoll.polling || applyChain?.status === 'running'}
          showSamples
        />
      </CardContent>
    </Card>
  );
}
